import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import {
  ACCOUNT_SECTION_IDS,
  type AccountSectionConfiguration,
  type AccountSectionId,
} from '../types/spark-options';
import { dedupeKnown, normalizeOrder } from './order';

/** Reports each `order`/`hide` entry dropped for not being a known section id. */
export type AccountSectionDropReporter = (
  field: 'order' | 'hide',
  index: number,
  value: unknown,
) => void;

const ACCOUNT_SECTIONS_EDITOR =
  'features:storefront_options.components.account_sections_editor' satisfies I18nPrefix;

export type AccountSectionItem = {
  id: AccountSectionId;
  hidden: boolean;
};

export type NormalizedAccountSectionConfiguration = {
  order: AccountSectionId[];
  hide: AccountSectionId[];
};

type RawAccountSectionConfiguration = {
  order?: readonly unknown[];
  hide?: readonly unknown[];
};

const ACCOUNT_SECTION_TITLE_KEYS = {
  'spark-slot-my-account': 'spark_slot_my_account',
  'recent-activity': 'recent_activity',
  'shopping-lists': 'shopping_lists',
  financials: 'financials',
  'my-details': 'my_details',
  reports: 'reports',
  'address-book': 'address_book',
  'company-users': 'company_users',
} as const satisfies Record<AccountSectionId, string>;

export function getAccountSectionTitle(id: AccountSectionId): string {
  return t(`${ACCOUNT_SECTIONS_EDITOR}.sections.${ACCOUNT_SECTION_TITLE_KEYS[id]}`);
}

export function normalizeAccountSectionConfiguration(
  config: RawAccountSectionConfiguration | null | undefined,
  onDrop?: AccountSectionDropReporter,
): NormalizedAccountSectionConfiguration {
  return {
    order: normalizeOrder(
      config?.order,
      ACCOUNT_SECTION_IDS,
      onDrop && ((index, value) => onDrop('order', index, value)),
    ),
    hide: dedupeKnown(
      config?.hide,
      ACCOUNT_SECTION_IDS,
      onDrop && ((index, value) => onDrop('hide', index, value)),
    ),
  };
}

export function toAccountSectionItems(
  config: RawAccountSectionConfiguration | null | undefined,
): AccountSectionItem[] {
  const normalized = normalizeAccountSectionConfiguration(config);
  const hiddenIds = new Set(normalized.hide);

  return normalized.order.map((id) => ({
    id,
    hidden: hiddenIds.has(id),
  }));
}

export function toSparkAccountSectionConfiguration(
  items: AccountSectionItem[],
): AccountSectionConfiguration {
  const order = normalizeOrder(
    items.map((item) => item.id),
    ACCOUNT_SECTION_IDS,
  );
  const itemById = new Map(items.map((item) => [item.id, item]));

  return {
    order,
    hide: order.filter((id) => itemById.get(id)?.hidden),
  };
}
